import type { CSSProperties } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { useRouter } from 'next/navigation';
import type {
  Asset,
  VisualMatch as VisualMatchType,
} from '@frameio/graphql-client/generated';
import { date, plural } from '@frameio/i18n';
import { useIsMatchingClientName } from '@frameio/shared/contexts/ClientNameContext';
import { getThumbMedia } from '@frameio/shared/finder/utils';
import { getUploadDisplayDate } from '@frameio/shared/utils';
import { EMPTY_OBJECT } from '@frameio/utils';
import { formatBytes } from '@frameio/utils/formatters';
import {
  Button2 as Button,
  CommentCountLabel,
  IconButton2 as IconButton,
  MediaThumbnail,
  MediaThumbnailLabel,
  mergeProps,
  Text,
  theme,
  Tooltip,
  useEmulateButton,
  VersionNumberLabel,
} from '@frameio/vapor';
import {
  IconDownload,
  IconFolderArrowRight,
  IconLink,
  IconLock,
} from '@frameio/vapor-icons';
import { GLPYHS } from '../../../../constants';
import {
  ClipMatchTimeline,
  FolderThumb,
  ImageWrapper,
  MediaThumbnailContentBottom,
} from '../../../../shared/components';
import {
  useQuickSearchDownloadAssets,
  useQuickSearchModal,
  useTrackActionedOnResults,
  useTrackSearchResultsClicked,
} from '../../../../shared/contexts';
import { getTabSelected } from '../../../../shared/contexts/SearchAnalyticsContext/utils';
import type { QuickSearchAssetV2Fragment as QuickSearchAssetFragment } from '../../../../shared/gql/generated/QuickSearchAssetV2';
import { useAssetActions } from '../../../../shared/hooks';
import { useQuickSearchAssetData } from '../../../../shared/hooks/useQuickSearchAssetData';
import type { AssetResultMatch } from '../../../../shared/types';
import {
  getAssetDisplayCategory,
  getAssetDurationInfo,
  getAssetFileSize,
  getAssetResultItemLink,
  getDaysSinceCreated,
  getFileType,
  getMatchTypeTrackingValue,
  getMatchTypeTrackingValues,
  getResultSourceFromSectionId,
  getSortedMatchesSubset,
  getValidAssetResultMatches,
} from '../../../../shared/utils';
import { useQuickSearchAppliedFilterTypes } from '../../../hooks';
import { getActiveListItemDetails } from '../../../utils';
import styles from './AssetItemDetail.module.css';
import TEXT from './generated/TEXT/AssetItemDetailQS';
import { ItemDetailContainer } from './ItemDetailContainer';
import { ItemDetailMeta } from './ItemDetailMeta';
import { ItemDetailMatches } from './ItemDetailsMatches';
import { ItemDetailAvatar } from './shared';

const ASSET_THUMB_WIDTH = 284;
const ASSET_THUMB_HEIGHT = 160;
const folderThumbStyle = {
  '--asset-thumb-height': `${ASSET_THUMB_HEIGHT}px`,
  '--asset-thumb-width': `${ASSET_THUMB_WIDTH}px`,
} as CSSProperties;

TEXT.DEFINE({
  /** Button label to copy asset URL */
  copyUrl: 'Copy URL',
  /** Button label to view asset in its project */
  viewInProject: 'View in Project',
  /** Button label to view asset in trash/recently deleted */
  viewInTrash: 'Go to Recently Deleted',
  /** Button label to download asset */
  download: 'Download',
  /** Button label to import asset (used in Premiere client) */
  import: 'Import',
  /** Accessibility label for viewing asset in player */
  imageLabel: ({ name }: { name: string }): string => `View ${name} in player`,
  /** Label for restricted folder access */
  restricted: 'Restricted',
  /** Folder item count display with pluralization */
  itemCountLabel: ({ count }: { count: number }): string =>
    plural(count, `${count} items`, '1 item'),
  /** Prefix label for version number (e.g., "V1") */
  versionPrefixLabel: 'V',
  /** Formatted display date for asset (e.g., "Jan 26, 2026") */
  formattedDate: (displayDate: Date): string => date(displayDate, 'medium'),
  /** Accessible label for the comment count indicator (e.g. "5 comments"). */
  commentCountLabel: ({ count }: { count: number }): string =>
    plural(count, `${count} comments`, '1 comment'),
});

type AssetItemDetailProps = {
  focusedKey: string | null;
  onNavigate?: (href: string) => void;
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
};

export const AssetItemDetail = ({
  focusedKey,
  onNavigate,
  onScroll,
}: AssetItemDetailProps) => {
  const { isAssetDownloading } = useQuickSearchDownloadAssets();
  const router = useRouter();
  const ref = useRef<HTMLDivElement>(null);
  const isPremiereClient = useIsMatchingClientName('premiere');
  const trackSearchResultsClicked = useTrackSearchResultsClicked();
  const trackActionedOnResults = useTrackActionedOnResults();
  const { query: searchQuery } = useQuickSearchModal();

  const appliedFilterTypes = useQuickSearchAppliedFilterTypes();

  const {
    itemId: assetId = '',
    itemPosition: resultPosition = 1,
    sectionId,
  } = getActiveListItemDetails(focusedKey ? String(focusedKey) : null);

  // Fetch asset data and appropriate matches based on section
  const { assetData, matches } = useQuickSearchAssetData({
    assetId,
    sectionId,
  });

  const resultSource = getResultSourceFromSectionId(sectionId);
  const {
    canDownloadAsset,
    handleCopyLink,
    handleDownload,
    handleViewInProject,
    handleViewInTrash,
    isRecentlyDeleted,
  } = useAssetActions({
    appliedFilterTypes,
    assetId,
    resultPosition,
    resultSource,
    sectionId,
  });

  const isVersionStack = Number.isInteger(assetData?.versionNumber);
  const isFolderAsset = assetData?.__typename === 'FolderAsset';

  const name = assetData?.name ?? '';
  const creator = assetData?.creator;
  const isRestrictedFolder = isFolderAsset && assetData?.restricted;
  const folderItemCount = isFolderAsset ? assetData?.itemCount : null;
  const displayDate = isFolderAsset
    ? assetData?.insertedAt
    : getUploadDisplayDate(assetData);
  const fileType = getFileType(assetData as QuickSearchAssetFragment);
  const displayCategory = getAssetDisplayCategory(fileType?.type);
  const commentCount = assetData?.commentCount ?? 0;

  const formattedDate = displayDate
    ? TEXT.formattedDate(new Date(displayDate))
    : null;
  const folderItemCountLabel =
    typeof folderItemCount === 'number'
      ? TEXT.itemCountLabel({ count: folderItemCount })
      : null;
  const subtitleItems = [
    creator?.name,
    isFolderAsset ? folderItemCountLabel : formattedDate,
  ].filter(Boolean) as string[];
  const media =
    getThumbMedia(assetData as QuickSearchAssetFragment) ?? EMPTY_OBJECT;
  const durationInfo = getAssetDurationInfo(
    assetData as QuickSearchAssetFragment
  );
  const workspaceName = assetData?.project?.workspace?.name;
  const projectName = assetData?.project?.name;
  const baseMatches = getValidAssetResultMatches(matches?.nodes ?? []);

  const featuredMatches: AssetResultMatch[] =
    getSortedMatchesSubset(baseMatches);

  const visualMatch = featuredMatches.find(
    (match) => match.__typename === 'VisualMatch'
  ) as VisualMatchType;

  const fileSize = getAssetFileSize(assetData as QuickSearchAssetFragment);
  const formattedFileSize =
    typeof fileSize === 'number' ? formatBytes(fileSize) : null;
  const itemMeta = useMemo(
    () =>
      isFolderAsset
        ? {
            workspace: workspaceName,
            project: projectName,
            dateCreated: formattedDate,
            size: formattedFileSize,
            ...(isRestrictedFolder && { folderAccess: TEXT.restricted }),
          }
        : {
            workspace: workspaceName,
            project: projectName,
            size: formattedFileSize,
            fileType: fileType?.label,
          },
    [
      fileType?.label,
      formattedDate,
      formattedFileSize,
      isFolderAsset,
      isRestrictedFolder,
      projectName,
      workspaceName,
    ]
  );

  const handleOpenAsset = useCallback(() => {
    trackActionedOnResults?.({
      action: 'OpenInSource',
      assetCount: isFolderAsset ? 1 : undefined,
      filterApplied: appliedFilterTypes,
      folderCount: isFolderAsset ? undefined : 1,
      isBulkOperation: false,
      matchType: getMatchTypeTrackingValues(matches?.nodes ?? []),
      resultPosition,
      resultSource,
      resultType: isFolderAsset ? 'Folder' : 'Asset',
    });
    const href = getAssetResultItemLink(assetData as Asset, {
      isRecentlyDeleted,
    });
    router.push(href);
    onNavigate?.(href);
  }, [
    appliedFilterTypes,
    assetData,
    isFolderAsset,
    isRecentlyDeleted,
    matches?.nodes,
    onNavigate,
    resultPosition,
    resultSource,
    router,
    trackActionedOnResults,
  ]);

  /**
   * Track clicks on matches in the detail panel (Visual, Transcription, Comment, & Field Matches).
   * Fires account-search-result-clicked with source='SearchResultMatch'.
   */
  const handleMatchClick = useCallback(
    (matchTypename: AssetResultMatch['__typename']) => {
      const daysSinceCreated = getDaysSinceCreated(displayDate);
      const tabSelected = getTabSelected(sectionId);

      trackSearchResultsClicked?.({
        daysSinceUploaded: daysSinceCreated,
        filterApplied: appliedFilterTypes,
        matchType: getMatchTypeTrackingValue(matchTypename),
        resultName: name,
        resultPosition,
        resultSource,
        resultType: 'Asset',
        searchQuery,
        source: 'SearchResultMatch',
        tabSelected,
      });
    },
    [
      appliedFilterTypes,
      displayDate,
      name,
      resultPosition,
      resultSource,
      searchQuery,
      sectionId,
      trackSearchResultsClicked,
    ]
  );

  const titleRef = useRef<HTMLDivElement>(null);
  const { bind } = useEmulateButton(titleRef, 'link');

  const downloadLabel = isPremiereClient ? TEXT.import : TEXT.download;

  return (
    <ItemDetailContainer
      actionsComponent={
        isRecentlyDeleted ? (
          <Button
            iconBefore={<IconFolderArrowRight />}
            onPress={handleViewInTrash}
            size="small"
            variant="secondary"
          >
            {TEXT.viewInTrash}
          </Button>
        ) : (
          <div className={styles.actionsRow}>
            {canDownloadAsset && (
              <Tooltip title={downloadLabel}>
                <IconButton
                  icon={<IconDownload />}
                  isLoading={isAssetDownloading}
                  label={downloadLabel}
                  onPress={handleDownload}
                  size="small"
                  variant="secondary"
                />
              </Tooltip>
            )}
            <Button
              iconBefore={<IconLink />}
              onPress={handleCopyLink}
              size="small"
              variant="secondary"
            >
              {TEXT.copyUrl}
            </Button>
            <Button
              iconBefore={<IconFolderArrowRight />}
              onPress={handleViewInProject}
              size="small"
              variant="secondary"
            >
              {TEXT.viewInProject}
            </Button>
          </div>
        )
      }
      imageComponent={
        <ItemDetailAvatar
          ref={ref}
          aria-label={TEXT.imageLabel({ name })}
          display="flex"
          itemType={isFolderAsset ? 'folder' : 'asset'}
          justifyContent="center"
          onClick={handleOpenAsset}
          width="100%"
        >
          {isFolderAsset ? (
            <div className={styles.folderThumb} style={folderThumbStyle}>
              <FolderThumb
                folderId={assetId}
                height={ASSET_THUMB_HEIGHT}
                width={ASSET_THUMB_WIDTH}
              />
              {isRestrictedFolder && (
                <div
                  aria-hidden="true"
                  className={styles.folderRestrictionBadge}
                >
                  <IconLock
                    color={theme.colors.components.badge.icon}
                    size={16}
                  />
                </div>
              )}
            </div>
          ) : (
            <ImageWrapper hasBorder={false}>
              <MediaThumbnail
                assetTypename={assetData?.__typename ?? ''}
                contentBottom={
                  <MediaThumbnailContentBottom
                    contentBottom={
                      visualMatch && durationInfo ? (
                        <ClipMatchTimeline
                          clips={visualMatch.clips}
                          duration={durationInfo.originalValue}
                        />
                      ) : undefined
                    }
                    contentTopLeft={
                      commentCount > 0 && (
                        <CommentCountLabel
                          aria-label={TEXT.commentCountLabel({
                            count: commentCount,
                          })}
                          count={commentCount}
                        />
                      )
                    }
                    contentTopRight={
                      durationInfo ? (
                        <MediaThumbnailLabel>
                          {durationInfo.formattedValue}
                        </MediaThumbnailLabel>
                      ) : undefined
                    }
                    variant="item-detail"
                  />
                }
                contentTopRight={
                  isVersionStack ? (
                    <VersionNumberLabel
                      version={assetData?.versionNumber}
                      versionPrefixLabel={TEXT.versionPrefixLabel}
                    />
                  ) : undefined
                }
                displayCategory={displayCategory}
                height={ASSET_THUMB_HEIGHT}
                isImageFitPreferred={true}
                isScrubEnabled={
                  assetData?.__typename === 'VideoAsset' ||
                  assetData?.__typename === 'ModelAsset'
                }
                media={media}
                scrubBarWidth={2}
                width={ASSET_THUMB_WIDTH}
              />
            </ImageWrapper>
          )}
        </ItemDetailAvatar>
      }
      matchesComponent={
        Boolean(featuredMatches?.length) && (
          <ItemDetailMatches
            assetId={assetId}
            matches={featuredMatches}
            onMatchClick={handleMatchClick}
          />
        )
      }
      metaComponent={<ItemDetailMeta itemMeta={itemMeta} />}
      onScroll={onScroll}
      titleComponent={
        <div
          ref={titleRef}
          aria-label={TEXT.imageLabel({ name })}
          className={styles.titleBox}
          {...mergeProps({ onClick: handleOpenAsset }, bind)}
        >
          <Text
            enableTooltip={true}
            style={{ width: '100%' }}
            textAlign="center"
            truncate={2}
            variant="header"
          >
            {name}
          </Text>
          <SubtitleItems subtitleItems={subtitleItems} />
        </div>
      }
    />
  );
};

const SubtitleItems = ({ subtitleItems }: { subtitleItems: string[] }) => {
  if (subtitleItems.length === 0) {
    return null;
  }

  return (
    <div className={styles.subtitleItems}>
      {subtitleItems.map((subtitleItem, index) => (
        <span key={`${subtitleItem}-${index}`} className={styles.subtitleItem}>
          {index > 0 && (
            <Text color="tertiary" variant="secondary">
              {GLPYHS.bullet}
            </Text>
          )}
          <Text
            color="tertiary"
            enableTooltip={index === 0}
            truncate={index === 0}
            variant="secondary"
          >
            {subtitleItem}
          </Text>
        </span>
      ))}
    </div>
  );
};
